import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../lib/auth-context'
import LoadingSpinner from './LoadingSpinner'

/**
 * ProtectedRoute Component
 * 
 * Route protection wrapper that handles authentication and role-based access control.
 * 
 * Features:
 * - Authentication check: Redirects to /login if user is not authenticated
 * - Admin-only routes: Use requireAdmin prop for admin-only access
 * - Role-based access: Use allowedRoles prop for specific role access (e.g., Advisor Hub)
 * - Loading state: Shows loading spinner while checking authentication
 * 
 * Props:
 * @param {ReactNode} children - The component(s) to render if access is granted
 * @param {boolean} requireAdmin - If true, only admin role can access (default: false)
 * @param {Array<string>} allowedRoles - Array of allowed roles (e.g., ['advisor', 'investor', 'staff', 'admin'])
 * 
 * Usage Examples:
 * - Admin-only route: <ProtectedRoute requireAdmin={true}>...</ProtectedRoute>
 * - Role-based route: <ProtectedRoute allowedRoles={['advisor', 'investor', 'staff', 'admin']}>...</ProtectedRoute>
 * - Authenticated route: <ProtectedRoute>...</ProtectedRoute>
 * 
 * Redirects:
 * - Unauthenticated users → /login
 * - Unauthorized users (wrong role) → /dashboard
 */
const ProtectedRoute = ({ children, requireAdmin = false, allowedRoles = null }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return <LoadingSpinner />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Check if admin access is required
  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }

  // Check if specific roles are allowed
  if (allowedRoles && Array.isArray(allowedRoles) && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

export default ProtectedRoute
